/**
 * Saisonnalité de la fiche — M12 (retour Manon, mi-parcours).
 *
 * « Une saisonnalité possible d'intervention, pour que ça ne compte pas dans
 *   les 30 jours. » Un élagage déclaré en septembre ne se fait qu'en novembre :
 * on indique « intervention possible à partir du … » et les indicateurs (vue
 * du maire, rapport au conseil) comptent à partir de cette date.
 */
import { useState } from 'react';
import { Leaf } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Button, Input } from '@/components/ui';

const MOTIFS = ['Nidification (mars à août)', 'Sève montante', 'Hors gel', 'Période de végétation', 'Après la saison touristique'];

const dateFr = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

export function FicheSaisonSection({ signalementId, saisonOuverture, saisonMotif, onSaved }: {
  signalementId: string;
  saisonOuverture: string | null | undefined;
  saisonMotif: string | null | undefined;
  onSaved: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [date, setDate] = useState(saisonOuverture ?? '');
  const [motif, setMotif] = useState(saisonMotif ?? '');
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const aujourdhui = new Date().toISOString().slice(0, 10);
  const enAttente = !!saisonOuverture && saisonOuverture > aujourdhui;

  async function enregistrer(valeur: string | null) {
    setEnvoi(true); setErr(null);
    try {
      await api.serviceTechnique.signalements.definirSaison(signalementId, {
        saisonOuverture: valeur, saisonMotif: valeur ? (motif.trim() || null) : null,
      });
      setEdition(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiException ? e.payload.error : 'Enregistrement impossible');
    } finally { setEnvoi(false); }
  }

  return (
    <section className="mt-4 rounded-r-xl border border-border/60 p-4">
      <div className="flex items-center gap-2">
        <Leaf className="h-4 w-4 text-tech" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-ink">Saisonnalité</h2>
        {!edition && (
          <button type="button" className="ml-auto text-xs text-accent hover:underline" onClick={() => setEdition(true)}>
            {saisonOuverture ? 'Modifier' : 'Définir une saison'}
          </button>
        )}
      </div>

      {!edition && (saisonOuverture ? (
        <p className={`mt-2 text-sm ${enAttente ? 'text-tech' : 'text-ink-muted'}`}>
          Intervention possible à partir du <b>{dateFr(saisonOuverture)}</b>
          {saisonMotif ? ` — ${saisonMotif}` : ''}.
          <span className="block text-xs text-ink-muted">
            {enAttente
              ? 'En attente de saison : cette fiche ne compte pas encore dans les délais.'
              : 'Saison ouverte : les délais courent depuis cette date.'}
          </span>
        </p>
      ) : (
        <p className="mt-2 text-xs text-ink-muted">
          Aucune. Si l'intervention ne peut se faire qu'à une période donnée (élagage, taille…),
          indiquez-la : les délais ne compteront qu'à partir de cette date.
        </p>
      ))}

      {edition && (
        <div className="mt-3 space-y-2">
          <label className="block text-xs text-ink">
            Intervention possible à partir du
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" />
          </label>
          <label className="block text-xs text-ink">
            Motif (facultatif)
            <Input value={motif} maxLength={200} list="motifs-saison" placeholder="Ex : nidification"
              onChange={(e) => setMotif(e.target.value)} className="mt-1" />
          </label>
          <datalist id="motifs-saison">{MOTIFS.map((m) => <option key={m} value={m} />)}</datalist>
          {err && <p className="text-xs text-urgent">{err}</p>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="primary" disabled={envoi || !date} onClick={() => void enregistrer(date)}>Enregistrer</Button>
            {saisonOuverture && (
              <Button size="sm" variant="ghost" disabled={envoi} onClick={() => void enregistrer(null)}>Retirer la saison</Button>
            )}
            <Button size="sm" variant="ghost" disabled={envoi} onClick={() => setEdition(false)}>Annuler</Button>
          </div>
        </div>
      )}
    </section>
  );
}
